import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store/store';
import { PATHS } from '../../../constants/routes';
import SidePopup from '../SidePopup/SidePopup';
import { pushEcommerce } from '../../metrics/ym';
import classes from './CheckoutStatusPopup.module.css';

const POLL_MAX_ATTEMPTS = 50;
const POLL_TOTAL_DURATION_MS = 3 * 60 * 1000;
const POLL_INTERVAL_MS = Math.floor(POLL_TOTAL_DURATION_MS / (POLL_MAX_ATTEMPTS - 1));
const FRESH_SNAPSHOT_GRACE_MS = 5000;

const YM_PURCHASE_SNAPSHOT_KEY = 'yoo_purchase_snapshot_v1';
const CHECKOUT_RETURN_MARKER_KEY = 'checkout_popup_return_marker_v1';
const ACCOUNT_ORDERS_PATH = `${PATHS.account}/orders`;

type PopupMode = 'processing' | 'success' | 'pending';
type PopupKind = 'success' | 'pending';

type YmPurchaseProduct = {
    id: string;
    name: string;
    price?: number;
    quantity?: number;
};

type YmPurchaseSnapshot = {
    v: 1 | 2;
    orderNumber: string;
    paymentId: string;
    total: number | null;
    products: YmPurchaseProduct[];
    productIds?: string[];
    createdAt: number;
    userId?: string;
};

type ReturnMarker = {
    paymentId: string;
    orderNumber?: string;
    createdAt: number;
};

type OrderStatusResponse = {
    status: 'paid' | 'pending' | 'other';
    orderNumber: string;
    total: number | null;
};

function safeLocalGet(key: string): string {
    try {
        return localStorage.getItem(key) || '';
    } catch {
        return '';
    }
}

function safeLocalSet(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch {
        // ignore
    }
}

function safeLocalRemove(key: string): void {
    try {
        localStorage.removeItem(key);
    } catch {
        // ignore
    }
}

function safeSessionGet(key: string): string {
    try {
        return sessionStorage.getItem(key) || '';
    } catch {
        return '';
    }
}

function safeSessionRemove(key: string): void {
    try {
        sessionStorage.removeItem(key);
    } catch {
        // ignore
    }
}

function readPurchaseSnapshot(): YmPurchaseSnapshot | null {
    try {
        const raw = safeSessionGet(YM_PURCHASE_SNAPSHOT_KEY) || safeLocalGet(YM_PURCHASE_SNAPSHOT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as YmPurchaseSnapshot;
        if (!parsed || typeof parsed !== 'object') return null;
        if (parsed.v !== 1 && parsed.v !== 2) return null;
        if (!Array.isArray(parsed.products)) return null;
        return parsed;
    } catch {
        return null;
    }
}

function readReturnMarker(): ReturnMarker | null {
    try {
        const raw = safeSessionGet(CHECKOUT_RETURN_MARKER_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as ReturnMarker;
        if (!parsed || typeof parsed !== 'object') return null;
        return {
            paymentId: String(parsed.paymentId || '').trim(),
            orderNumber: String(parsed.orderNumber || '').trim(),
            createdAt: Number(parsed.createdAt || 0),
        };
    } catch {
        return null;
    }
}

function clearReturnMarker(): void {
    safeSessionRemove(CHECKOUT_RETURN_MARKER_KEY);
}

function toFiniteNumber(v: unknown): number | null {
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function normalizeProducts(products: YmPurchaseProduct[]): Array<{ id: string; name: string; price: number; quantity: number }> {
    if (!Array.isArray(products)) return [];
    return products
        .map((p) => {
            const id = String((p as any)?.id ?? '').trim();
            const name = String((p as any)?.name ?? '').trim();
            const priceRaw = toFiniteNumber((p as any)?.price);
            const qtyRaw = Number((p as any)?.quantity);
            const quantity = Number.isFinite(qtyRaw) && qtyRaw > 0 ? Math.floor(qtyRaw) : 1;
            if (!id || !name) return null;
            if (priceRaw == null || priceRaw <= 0) return null;
            return {
                id,
                name,
                price: Number(priceRaw.toFixed(2)),
                quantity,
            };
        })
        .filter(Boolean) as Array<{ id: string; name: string; price: number; quantity: number }>;
}

function normalizeProductIds(productIds: unknown): string[] {
    if (!Array.isArray(productIds)) return [];
    return Array.from(new Set(productIds.map((id) => String(id ?? '').trim()).filter(Boolean)));
}

function normalizeOrderStatus(value: unknown): 'paid' | 'pending' | 'other' {
    const normalized = String(value ?? '')
        .trim()
        .toLowerCase();
    if (normalized === 'paid' || normalized === 'assembled' || normalized === 'completed') return 'paid';
    if (normalized === 'pending') return 'pending';
    return 'other';
}

function resolveSnapshotUserId(snapshot: YmPurchaseSnapshot | null): string {
    return String(snapshot?.userId || '').trim();
}

function shouldSkipSnapshotForCurrentUser(snapshot: YmPurchaseSnapshot | null, currentUserId: string): boolean {
    const snapshotUserId = resolveSnapshotUserId(snapshot);
    if (!snapshotUserId) return false;
    if (!currentUserId) return true;
    return snapshotUserId !== currentUserId;
}

function getShownKey(kind: PopupKind, userId: string, paymentId: string): string {
    return `checkout_popup_${kind}_shown:${userId}:${paymentId}`;
}

function hasShown(kind: PopupKind, userId: string, paymentId: string): boolean {
    if (!userId || !paymentId) return false;
    return safeLocalGet(getShownKey(kind, userId, paymentId)) === '1';
}

function markShown(kind: PopupKind, userId: string, paymentId: string): void {
    if (!userId || !paymentId) return;
    safeLocalSet(getShownKey(kind, userId, paymentId), '1');
}

const CheckoutStatusPopup: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);
    const authUserId = useSelector((s: RootState) => {
        const user = s.auth.user;
        if (!user || typeof user !== 'object') return '';
        const record = user as Record<string, unknown>;
        const id = typeof record._id === 'string' ? record._id : typeof record.id === 'string' ? record.id : '';
        return id.trim();
    });

    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState<PopupMode>('processing');
    const [orderNum, setOrderNum] = useState('SG-XXXXXX');
    const [orderTotal, setOrderTotal] = useState<number | null>(null);
    const [activePaymentId, setActivePaymentId] = useState('');
    const [checkTick, setCheckTick] = useState(0);

    const intervalRef = useRef<number | null>(null);
    const attemptsRef = useRef(0);
    const inFlightRef = useRef(false);
    const activeFlowKeyRef = useRef('');

    const requestRecheck = useCallback(() => {
        setCheckTick((prev) => prev + 1);
    }, []);

    const stopPolling = useCallback(() => {
        if (intervalRef.current != null) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        attemptsRef.current = 0;
        inFlightRef.current = false;
    }, []);

    const ensurePurchaseAnalyticsSent = useCallback((paymentId: string, currentOrderNumber: string, currentOrderTotal: number | null) => {
        const purchaseId = String(paymentId || currentOrderNumber || '').trim();
        if (!purchaseId) return;

        const sentKey = `ym_purchase_sent:${purchaseId}`;
        const cleanupKey = `cart_paid_cleanup_done:${purchaseId}`;
        const snap = readPurchaseSnapshot();
        const snapProducts = normalizeProducts(snap?.products || []);
        const paidProductIds = normalizeProductIds(snap?.productIds);
        const cleanupDone = safeLocalGet(cleanupKey) === '1';
        const canDropSnapshot = cleanupDone || paidProductIds.length === 0;

        if (safeLocalGet(sentKey) === '1') {
            if (canDropSnapshot) {
                safeSessionRemove('yoo_payment_id');
                safeSessionRemove('yoo_order_num');
                safeSessionRemove(YM_PURCHASE_SNAPSHOT_KEY);
                safeLocalRemove(YM_PURCHASE_SNAPSHOT_KEY);
            }
            return;
        }

        try {
            const revenueFromState =
                typeof currentOrderTotal === 'number' && Number.isFinite(currentOrderTotal) && currentOrderTotal > 0 ? Number(currentOrderTotal.toFixed(2)) : null;

            const revenueFromSnapTotal = typeof snap?.total === 'number' && Number.isFinite(snap.total) && snap.total > 0 ? Number(snap.total.toFixed(2)) : null;

            const revenueFromProducts =
                snapProducts.length > 0
                    ? Number(
                          snapProducts
                              .reduce((sum, product) => {
                                  const price = typeof product.price === 'number' ? product.price : 0;
                                  const quantity = typeof product.quantity === 'number' ? product.quantity : 1;
                                  return sum + price * quantity;
                              }, 0)
                              .toFixed(2),
                      )
                    : null;

            const revenue = revenueFromState ?? revenueFromSnapTotal ?? revenueFromProducts;

            const products =
                snapProducts.length > 0
                    ? snapProducts
                    : [
                          {
                              id: `order:${purchaseId}`,
                              name: 'Заказ',
                              price: revenue ?? 1,
                              quantity: 1,
                          },
                      ];

            const actionField: Record<string, unknown> = { id: String(purchaseId) };
            if (revenue != null) {
                actionField.revenue = revenue;
            }

            pushEcommerce({
                ecommerce: {
                    currencyCode: 'RUB',
                    purchase: {
                        actionField,
                        products,
                    },
                },
            });

            pushEcommerce({ ecommerce: {} as Record<string, unknown> });
            safeLocalSet(sentKey, '1');
            safeSessionRemove('yoo_payment_id');
            safeSessionRemove('yoo_order_num');

            if (canDropSnapshot) {
                safeSessionRemove(YM_PURCHASE_SNAPSHOT_KEY);
                safeLocalRemove(YM_PURCHASE_SNAPSHOT_KEY);
            }
        } catch (e) {
            console.error('Не удалось отправить e-commerce событие покупки в Метрику', e);
        }
    }, []);

    const fetchOrderStatus = useCallback(async (paymentId: string): Promise<OrderStatusResponse | null> => {
        if (!paymentId) return null;
        try {
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/orders/by-payment/${paymentId}`, {
                credentials: 'include',
            });

            if (response.status === 401 || response.status === 403) return null;
            if (!response.ok) return null;

            const data = await response.json();
            const rawTotal =
                typeof data?.totalPrice === 'number'
                    ? data.totalPrice
                    : typeof data?.totalAmount === 'number'
                      ? data.totalAmount
                      : typeof data?.amount === 'number'
                        ? data.amount
                        : null;

            return {
                status: normalizeOrderStatus(data?.orderStatus),
                orderNumber: String(data?.orderNumber ?? '').trim(),
                total: typeof rawTotal === 'number' && Number.isFinite(rawTotal) ? Number(rawTotal.toFixed(2)) : null,
            };
        } catch (e) {
            console.error('CheckoutStatusPopup: fetchOrderStatus failed', e);
            return null;
        }
    }, []);

    const finalizeSuccess = useCallback(
        (paymentId: string, nextOrderNum: string, nextTotal: number | null) => {
            stopPolling();
            clearReturnMarker();
            if (authUserId) {
                markShown('success', authUserId, paymentId);
            }
            setActivePaymentId(paymentId);
            if (nextOrderNum) setOrderNum(nextOrderNum);
            if (nextTotal != null) setOrderTotal(nextTotal);
            setMode('success');
            setOpen(true);
            activeFlowKeyRef.current = `success:${paymentId}`;
            ensurePurchaseAnalyticsSent(paymentId, nextOrderNum, nextTotal);
        },
        [authUserId, ensurePurchaseAnalyticsSent, stopPolling],
    );

    const finalizePending = useCallback(
        (paymentId: string, nextOrderNum: string, nextTotal: number | null) => {
            stopPolling();
            clearReturnMarker();
            if (authUserId) {
                markShown('pending', authUserId, paymentId);
            }
            setActivePaymentId(paymentId);
            if (nextOrderNum) setOrderNum(nextOrderNum);
            if (nextTotal != null) setOrderTotal(nextTotal);
            setMode('pending');
            setOpen(true);
            activeFlowKeyRef.current = `pending:${paymentId}`;
        },
        [authUserId, stopPolling],
    );

    const startProcessingFlow = useCallback(
        (snapshot: YmPurchaseSnapshot) => {
            const paymentId = String(snapshot.paymentId || '').trim();
            if (!paymentId) return;

            const flowKey = `processing:${paymentId}`;
            if (activeFlowKeyRef.current === flowKey && open && mode === 'processing') return;

            stopPolling();
            activeFlowKeyRef.current = flowKey;
            setActivePaymentId(paymentId);
            setOrderNum(String(snapshot.orderNumber || '').trim() || 'SG-XXXXXX');
            setOrderTotal(typeof snapshot.total === 'number' && Number.isFinite(snapshot.total) ? Number(snapshot.total.toFixed(2)) : null);
            setMode('processing');
            setOpen(true);

            let lastExplicitPending: OrderStatusResponse | null = null;

            const tick = async () => {
                if (inFlightRef.current) return;
                inFlightRef.current = true;
                attemptsRef.current += 1;
                try {
                    const result = await fetchOrderStatus(paymentId);

                    if (result?.orderNumber) setOrderNum(result.orderNumber);
                    if (result?.total != null) setOrderTotal(result.total);

                    if (result?.status === 'paid') {
                        finalizeSuccess(paymentId, result.orderNumber || snapshot.orderNumber || '', result.total);
                        return;
                    }

                    if (result?.status === 'pending') {
                        lastExplicitPending = result;
                    }

                    if (result?.status === 'other') {
                        stopPolling();
                        clearReturnMarker();
                        setOpen(false);
                        activeFlowKeyRef.current = '';
                        return;
                    }

                    if (attemptsRef.current >= POLL_MAX_ATTEMPTS) {
                        if (lastExplicitPending?.status === 'pending') {
                            finalizePending(
                                paymentId,
                                lastExplicitPending.orderNumber || snapshot.orderNumber || '',
                                lastExplicitPending.total ?? snapshot.total ?? null,
                            );
                        } else {
                            stopPolling();
                            setOpen(false);
                            activeFlowKeyRef.current = '';
                        }
                    }
                } finally {
                    inFlightRef.current = false;
                }
            };

            void tick();
            intervalRef.current = window.setInterval(() => {
                void tick();
            }, POLL_INTERVAL_MS);
        },
        [fetchOrderStatus, finalizePending, finalizeSuccess, mode, open, stopPolling],
    );

    const checkSnapshotOnce = useCallback(
        async (snapshot: YmPurchaseSnapshot, successShown: boolean, pendingShown: boolean) => {
            const paymentId = String(snapshot.paymentId || '').trim();
            if (!paymentId) return;

            const result = await fetchOrderStatus(paymentId);
            if (!result) return;

            if (result.status === 'paid') {
                if (!successShown) {
                    finalizeSuccess(paymentId, result.orderNumber || snapshot.orderNumber || '', result.total);
                } else {
                    ensurePurchaseAnalyticsSent(paymentId, result.orderNumber || snapshot.orderNumber || '', result.total);
                }
                return;
            }

            if (result.status === 'pending' && !pendingShown) {
                finalizePending(paymentId, result.orderNumber || snapshot.orderNumber || '', result.total);
            }
        },
        [ensurePurchaseAnalyticsSent, fetchOrderStatus, finalizePending, finalizeSuccess],
    );

    useEffect(() => {
        const onFocus = () => requestRecheck();
        const onOnline = () => requestRecheck();
        const onPageShow = () => requestRecheck();
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                requestRecheck();
            }
        };

        window.addEventListener('focus', onFocus);
        window.addEventListener('online', onOnline);
        window.addEventListener('pageshow', onPageShow);
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('online', onOnline);
            window.removeEventListener('pageshow', onPageShow);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [requestRecheck]);

    useEffect(() => {
        if (!isAuthenticated || !authUserId) {
            stopPolling();
            setOpen(false);
            activeFlowKeyRef.current = '';
            return;
        }

        const snapshot = readPurchaseSnapshot();
        if (!snapshot) return;
        if (shouldSkipSnapshotForCurrentUser(snapshot, authUserId)) return;

        const paymentId = String(snapshot.paymentId || '').trim();
        if (!paymentId) return;

        const returnMarker = readReturnMarker();
        const successAlreadyShown = hasShown('success', authUserId, paymentId);
        const pendingAlreadyShown = hasShown('pending', authUserId, paymentId);

        const snapshotCreatedAt = Number(snapshot.createdAt || 0);
        const snapshotAgeMs = Number.isFinite(snapshotCreatedAt) ? Date.now() - snapshotCreatedAt : Number.POSITIVE_INFINITY;
        const isFreshSnapshot = !returnMarker && snapshotAgeMs >= 0 && snapshotAgeMs < FRESH_SNAPSHOT_GRACE_MS;

        if (isFreshSnapshot) {
            const remainingMs = Math.max(0, FRESH_SNAPSHOT_GRACE_MS - snapshotAgeMs);
            const timeoutId = window.setTimeout(() => {
                requestRecheck();
            }, remainingMs);
            return () => window.clearTimeout(timeoutId);
        }

        const isReturnFlow = location.pathname === ACCOUNT_ORDERS_PATH && returnMarker?.paymentId === paymentId;

        if (isReturnFlow && !successAlreadyShown) {
            startProcessingFlow(snapshot);
            return;
        }

        if (!successAlreadyShown || !pendingAlreadyShown) {
            void checkSnapshotOnce(snapshot, successAlreadyShown, pendingAlreadyShown);
        }
    }, [authUserId, checkSnapshotOnce, checkTick, isAuthenticated, location.pathname, requestRecheck, startProcessingFlow, stopPolling]);

    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [stopPolling]);

    const handleClose = useCallback(() => {
        stopPolling();
        clearReturnMarker();
        setOpen(false);
        activeFlowKeyRef.current = '';
    }, [stopPolling]);

    const handleSuccessAction = useCallback(() => {
        handleClose();
        if (location.pathname !== PATHS.showcase) {
            navigate(PATHS.showcase);
        }
    }, [handleClose, location.pathname, navigate]);

    const handlePendingAction = useCallback(() => {
        handleClose();
        if (location.pathname !== ACCOUNT_ORDERS_PATH) {
            navigate(ACCOUNT_ORDERS_PATH);
        }
    }, [handleClose, location.pathname, navigate]);

    const popupTitle = useMemo(() => {
        if (mode === 'success') return 'Заказ оформлен';
        if (mode === 'pending') return 'Заказ ждёт оплаты';
        return 'Оплата обрабатывается…';
    }, [mode]);

    const processingText = useMemo(() => {
        const hasNumber = String(orderNum || '').trim() && orderNum !== 'SG-XXXXXX';
        return hasNumber ? `Подтверждаем оплату заказа № ${orderNum}, подождите немного…` : 'Подтверждаем платёж, подождите немного…';
    }, [orderNum]);

    if (!isAuthenticated || !authUserId) return null;

    return (
        <SidePopup open={open} onClose={handleClose} title={popupTitle} width={555} minWidth={360} maxWidth={555} className={classes.popup}>
            <div className={classes.content}>
                {mode === 'success' ? (
                    <>
                        <div className={classes.textBlock}>
                            <p className={classes.text}>
                                Ваш заказ успешно оформлен. <br />
                                Благодарим за покупку.
                            </p>
                            <p className={classes.text}>Отследить статус заказа можно в Личном кабинете в разделе ЗАКАЗЫ.</p>
                        </div>

                        <button type="button" className={classes.actionButton} onClick={handleSuccessAction}>
                            К покупкам
                        </button>
                    </>
                ) : mode === 'pending' ? (
                    <>
                        <div className={classes.textBlock}>
                            <p className={classes.text}>Ваш заказ не оплачен.</p>
                            <p className={classes.text}>Для оплаты перейдите в Личный кабинет в раздел ЗАКАЗЫ.</p>
                        </div>

                        <button type="button" className={classes.actionButton} onClick={handlePendingAction}>
                            К заказу
                        </button>
                    </>
                ) : (
                    <div className={classes.processingBlock}>
                        <div className={classes.spinner} aria-hidden="true" />
                        <p className={classes.processingText}>{processingText}</p>
                        {activePaymentId ? <p className={classes.processingMeta}>Проверяем статус платежа…</p> : null}
                        {typeof orderTotal === 'number' && Number.isFinite(orderTotal) ? (
                            <p className={classes.processingMeta}>Сумма заказа: {orderTotal.toLocaleString('ru-RU')} ₽</p>
                        ) : null}
                    </div>
                )}
            </div>
        </SidePopup>
    );
};

export default CheckoutStatusPopup;
